import { APP_ID, inject } from '@angular/core';
import { CanMatchFn } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { map } from 'rxjs';
import { AuthStore } from '../store/auth-store.service';
import { RbacService } from '../rbac/rbac.service';
import { DESKTOP_ONLY_ROLES } from '../rbac/rbac-config';
import { waitForSession } from './session-ready';
import {
  injectControlHubDesktopUrl,
  injectControlHubUrl,
} from '@seatix/shared/core';

/**
 * Redirects users to the correct control-hub app based on their role.
 *
 * When the user has no localStorage auth (e.g. after a cross-app redirect that
 * called clearAuth(), or a fresh session), the guard waits for the silent
 * re-authentication via /api/auth/me (session cookie) to settle before
 * deciding (waitForSession) — otherwise it would always see
 * isAuthenticated()=false and let the wrong app load.
 *
 * Clears local auth before redirecting to prevent getting stuck in a redirect
 * loop when stale auth from a previous session exists in localStorage.
 */
export const crossAppRedirectGuard: CanMatchFn = () => {
  const authStore = inject(AuthStore);
  const rbacService = inject(RbacService);
  const doc = inject(DOCUMENT);
  const appId = inject(APP_ID);
  const desktopUrl = injectControlHubDesktopUrl();
  const webUrl = injectControlHubUrl();

  const isDesktopApp = appId === 'control-hub-desktop';

  function checkAndRedirect(): boolean {
    if (!authStore.isAuthenticated()) {
      return true;
    }

    const primaryRole = rbacService.primaryRole();
    if (!primaryRole) {
      return true;
    }

    const isDesktopRole = DESKTOP_ONLY_ROLES.includes(primaryRole);
    const shouldRedirect = isDesktopApp ? !isDesktopRole : isDesktopRole;

    if (!shouldRedirect) {
      return true;
    }

    authStore.clearAuth();

    const targetUrl = isDesktopApp ? webUrl : desktopUrl;
    doc.location.href = targetUrl;
    return false;
  }

  if (authStore.isAuthenticated()) {
    return checkAndRedirect();
  }

  return waitForSession().pipe(map(() => checkAndRedirect()));
};
